import { v4 } from "uuid";

import { setChatId } from "../../utils/chat/chatId";
import { getItineraryDetailsAPI } from "../getItineraryDetails";
import { clonePublicTravelPlanAPI } from "./clonePublicTravelPlanAPI";

interface CloneSharedTravelPlanArgs {
  selectedChatData: any;
  accessJWT: string;
  userId: string | undefined | null;
}

interface CloneSharedTravelPlanResult {
  // The updated selectedChatData if a clone (or already-owned recognition)
  // happened, otherwise the original reference unchanged.
  next: any;
  // True only when a fresh clone landed in this call; false on no-op,
  // failure, and on the already-owned branch.
  cloned: boolean;
  // True when the helper recognized the viewer as the source itinerary's
  // owner and skipped the clone (next still gets travelPlanId stamped to
  // the source id so subsequent share / book flows treat it as owned).
  alreadyOwned: boolean;
  // The id stamped onto next.travelPlanId — either the new clone id (when
  // cloned) or the source id (when alreadyOwned). Undefined on no-op /
  // failure. Callers use this to mirror travelPlanId onto matching chatData
  // entries so re-derivation of selectedChatData from chatData doesn't
  // resurrect sharedSourceTravelPlanId and trigger another clone.
  travelPlanId?: string;
}

// Shared "clone-on-first-interaction" used by both the chat-send and Book
// flows on a /shared/:id viewer session. ViewShareItinerary stamps the
// source itinerary id onto `selectedChatData.sharedSourceTravelPlanId`;
// the first interaction (chat send or Book click) calls this helper.
//
// Behavior:
//   1. Fetch the source itinerary to check ownership.
//   2. If the viewer already owns the source row (source.userId === userId),
//      skip the clone — stamp travelPlanId = sourceId, mark isSaved=true,
//      clear sharedSourceTravelPlanId. No POST to the save endpoint, no
//      fresh chat session id (the viewer's existing session keeps its
//      LLM memory).
//   3. Otherwise, clone the source into the viewer's account via the
//      legacy itinerary endpoints (clonePublicTravelPlanAPI), generate a
//      fresh _zenvoya_chat_id so the WS payload threads under the clone's
//      session, and stamp travelPlanId = newId.
//   4. In both branches, history.replaceState off /shared/:id so a
//      refresh doesn't re-trigger the share-viewer flow.
//
// Caller owns `setSelectedChatData(next)` AND must mirror travelPlanId +
// clear sharedSourceTravelPlanId on matching chatData entries — otherwise
// the synthetic greeting bubble retains the stale flag and the helper
// re-fires on the next send.
export async function cloneSharedTravelPlanIfNeeded({
  selectedChatData,
  accessJWT,
  userId,
}: CloneSharedTravelPlanArgs): Promise<CloneSharedTravelPlanResult> {
  const sourceId = selectedChatData?.sharedSourceTravelPlanId;
  // eslint-disable-next-line no-console
  console.log("[CLONE-HELPER] gate inputs:", {
    sourceId,
    existingTravelPlanId: selectedChatData?.travelPlanId,
    viewerUserId: userId,
    hasJWT: !!accessJWT,
    selectedChatDataId: selectedChatData?.id,
  });
  if (!sourceId) {
    // eslint-disable-next-line no-console
    console.log("[CLONE-HELPER] no-op: no sharedSourceTravelPlanId");
    return { next: selectedChatData, cloned: false, alreadyOwned: false };
  }
  if (selectedChatData?.travelPlanId) {
    // eslint-disable-next-line no-console
    console.log("[CLONE-HELPER] no-op: travelPlanId already set →", selectedChatData.travelPlanId);
    return { next: selectedChatData, cloned: false, alreadyOwned: false };
  }
  if (!userId || !accessJWT) {
    // eslint-disable-next-line no-console
    console.log("[CLONE-HELPER] no-op: missing userId or accessJWT");
    return { next: selectedChatData, cloned: false, alreadyOwned: false };
  }

  // Fetch the source itinerary so we can compare userId before deciding
  // whether to actually clone. Uses the same endpoint clonePublicTravelPlanAPI
  // would call internally — one extra GET, but only on first interaction.
  const sourceRes: any = await getItineraryDetailsAPI(sourceId, accessJWT);
  const sourceUserId = sourceRes?.data?.userId;
  // eslint-disable-next-line no-console
  console.log("[CLONE-HELPER] source fetch:", {
    sourceUserId,
    viewerUserId: userId,
    match: sourceUserId === userId,
    sourceDataKeys: sourceRes?.data ? Object.keys(sourceRes.data) : null,
  });

  // Already mine — skip the clone, stamp the source id onto travelPlanId
  // and mark as saved so future share / book flows recognize ownership.
  // Do NOT rotate _zenvoya_chat_id here: the viewer's existing chat
  // session and its server-side memory should be preserved.
  if (sourceUserId && sourceUserId === userId) {
    // eslint-disable-next-line no-console
    console.log("[CLONE-HELPER] alreadyOwned branch — skipping clone, stamping travelPlanId=", sourceId);
    const next = {
      ...selectedChatData,
      travelPlanId: sourceId,
      sharedSourceTravelPlanId: undefined,
      isSaved: true,
    };
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/shared/")) {
      try {
        window.history.replaceState(null, "", "/");
      } catch {
        // ignore — non-fatal, the recognition itself is what matters.
      }
    }
    return { next, cloned: false, alreadyOwned: true, travelPlanId: sourceId };
  }

  // Different user — clone into the viewer's account.
  // eslint-disable-next-line no-console
  console.log("[CLONE-HELPER] cloning branch — viewer is NOT source owner, calling clone API");
  try {
    const response: any = await clonePublicTravelPlanAPI(accessJWT, sourceId, userId);
    const newId = response?.data?.id;
    // eslint-disable-next-line no-console
    console.log("[CLONE-HELPER] clone API returned newId=", newId);
    if (!newId) return { next: selectedChatData, cloned: false, alreadyOwned: false };

    setChatId(v4());

    // Intentionally do NOT set `isSaved: true` here for the clone branch.
    // saveItineraryBeforeBooking keys POST-vs-PUT off `isSaved`; flipping
    // it true here would force a PUT against an Itinerary id under a
    // userId binding the booking flow expects to match selectedChatData.id
    // (chat bubble), not travelPlanId. Let the Book flow's POST run and
    // flip isSaved naturally.
    const next = {
      ...selectedChatData,
      travelPlanId: newId,
      sharedSourceTravelPlanId: undefined,
    };

    if (typeof window !== "undefined" && window.location.pathname.startsWith("/shared/")) {
      try {
        window.history.replaceState(null, "", "/");
      } catch {
        // ignore — non-fatal, the clone itself is what matters.
      }
    }

    return { next, cloned: true, alreadyOwned: false, travelPlanId: newId };
  } catch {
    return { next: selectedChatData, cloned: false, alreadyOwned: false };
  }
}
